import React, { useEffect, useState } from 'react';
import { API_URL } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Button from '../ui/Button';
import { FileText, Trash2, Edit, X, Calendar, Plus } from 'lucide-react';

interface CV {
    _id: string;
    title: string;
    updatedAt: string;
    template: string;
}

interface CVHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectCV: (id: string) => void;
    onNewCV: () => void;
}

const CVHistoryModal: React.FC<CVHistoryModalProps> = ({ isOpen, onClose, onSelectCV, onNewCV }) => {
    const { user, token } = useAuth();
    const [cvs, setCvs] = useState<CV[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchCVs = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${API_URL}/cv/user/${user?._id}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) throw new Error('Impossible de récupérer les CVs');

                const data = await response.json();
                setCvs(data);
            } catch (err) {
                console.error('Error fetching CVs:', err);
                setError('Impossible de charger vos CVs');
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            fetchCVs();
        }
    }, [isOpen, user?._id, token]);

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce CV ?')) return;

        try {
            const response = await fetch(`${API_URL}/cv/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                setCvs(cvs.filter(cv => cv._id !== id));
            }
        } catch (err) {
            console.error('Error deleting CV:', err);
            alert('Erreur lors de la suppression');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl animate-fade-in">
                {/* Modal Header */}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white rounded-t-2xl z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800">Mes CVs</h2>
                        <p className="text-sm text-slate-500">Gérez votre historique de CVs</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                        <X className="w-6 h-6 text-slate-400" />
                    </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto flex-1 bg-slate-50/50">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 text-red-600 p-4 rounded-xl text-center">
                            {error}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* New CV Card */}
                            <div
                                onClick={onNewCV}
                                className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all min-h-[200px] group"
                            >
                                <div className="p-3 bg-indigo-50 rounded-full group-hover:bg-indigo-100 transition-colors">
                                    <Plus className="w-6 h-6 text-indigo-600" />
                                </div>
                                <span className="font-semibold text-sm text-slate-600 group-hover:text-indigo-600">Nouveau CV</span>
                            </div>

                            {/* CV List */}
                            {cvs.map((cv) => (
                                <div
                                    key={cv._id}
                                    onClick={() => onSelectCV(cv._id)}
                                    className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer overflow-hidden group flex flex-col"
                                >
                                    <div className="h-24 bg-slate-100 relative overflow-hidden flex items-center justify-center">
                                        <FileText className="w-10 h-10 text-slate-300" />
                                        <div className="absolute inset-0 bg-indigo-900/0 group-hover:bg-indigo-900/5 transition-colors" />
                                    </div>

                                    <div className="p-4 flex-1 flex flex-col">
                                        <div className="mb-3 flex-1">
                                            <h3 className="font-bold text-slate-800 line-clamp-1 group-hover:text-indigo-600 transition-colors">
                                                {cv.title}
                                            </h3>
                                            <div className="flex items-center gap-1.5 text-[10px] text-slate-400 mt-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(cv.updatedAt).toLocaleDateString()}
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                className="flex-1 text-xs h-7"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSelectCV(cv._id);
                                                }}
                                            >
                                                <Edit className="w-3 h-3 mr-1" />
                                                Modifier
                                            </Button>
                                            <Button
                                                variant="danger"
                                                className="h-7 w-7 !p-0 flex items-center justify-center"
                                                onClick={(e) => handleDelete(cv._id, e)}
                                            >
                                                <Trash2 className="w-4 h-4 text-white" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CVHistoryModal;
